import {
  Calendar,
  Mail,
  MapPin,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Pin,
  PinOff,
  UserSearch,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  useCallerIdPinStatus,
  usePinCallerId,
  useProspectDetail,
  useUnpinCallerId,
} from '@/api/powerDialer'
import { styleForCategory } from '@/lib/dispositionCategory'
import type { PowerDialerProspect } from '@/types/powerDialer'

function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatTime(dateStr: string, locale: string): string {
  const d = new Date(dateStr)
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

/** "Aujourd'hui" / "Hier" / "15 juil." — libellé de jour relatif. */
function relativeDay(
  dateStr: string,
  locale: string,
  labels: { today: string; yesterday: string },
): string {
  const d = new Date(dateStr)
  const now = new Date()
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate())
  const diff = Math.round(
    (startOfDay(now).getTime() - startOfDay(d).getTime()) / 86400000,
  )
  if (diff === 0) return labels.today
  if (diff === 1) return labels.yesterday
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
}

interface Props {
  campaignId: number
  activeProspect: PowerDialerProspect | null
  darkMode: boolean
}

export function PdProspectDetail({ campaignId, activeProspect, darkMode }: Props) {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'
  const dayLabels = {
    today: t('powerDialer.session.today', "Aujourd'hui"),
    yesterday: t('powerDialer.session.yesterday', 'Hier'),
  }
  const detailQuery = useProspectDetail(
    campaignId,
    activeProspect ? activeProspect.id : null,
  )
  const callLogs = detailQuery.data?.call_logs || []

  // Caller ID mémorisé pour ce contact (sticky auto ou figé manuellement).
  const contactPhone = activeProspect?.phone
  const pinStatus = useCallerIdPinStatus(campaignId, contactPhone)
  const pinCaller = usePinCallerId(campaignId)
  const unpinCaller = useUnpinCallerId(campaignId)

  const togglePin = () => {
    if (!contactPhone || !pinStatus.data?.caller_id) return
    if (pinStatus.data.pinned) {
      unpinCaller.mutate(contactPhone, {
        onSuccess: () => toast.success(t('powerDialer.session.unpinnedToast', 'Numéro libéré (sticky automatique)')),
      })
    } else {
      pinCaller.mutate(
        { contact_phone: contactPhone, caller_id: pinStatus.data.caller_id },
        { onSuccess: () => toast.success(t('powerDialer.session.pinnedToast', 'Numéro figé pour ce contact')) },
      )
    }
  }

  const border = darkMode ? 'border-slate-800' : 'border-slate-200'
  const cardBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const textSecondary = darkMode ? 'text-slate-300' : 'text-slate-600'
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400'
  const textFaint = darkMode ? 'text-slate-700' : 'text-slate-300'
  const bg = darkMode ? 'bg-slate-950' : 'bg-slate-50'

  if (!activeProspect) {
    return (
      <div className={cn('flex-1 flex items-center justify-center', bg)}>
        <div className="text-center space-y-3">
          <UserSearch className={cn('w-12 h-12 mx-auto', textFaint)} />
          <p className={cn('text-sm', textMuted)}>{t('powerDialer.session.selectProspect', 'Sélectionnez un prospect')}</p>
        </div>
      </div>
    )
  }

  // Badge piloté par la disposition appliquée (sa catégorie donne couleur +
  // libellé). Rien tant que le prospect n'a pas de disposition (en attente).
  const disposition = activeProspect.disposition
  const showBadge = !!disposition
  const badgeStyle = disposition
    ? styleForCategory(disposition.category)
    : null
  const badgeLabel = disposition?.name ?? ''

  // Initiales pour l'avatar (2 lettres max, à partir du nom sinon du numéro).
  const displayName =
    activeProspect.full_name ||
    `${activeProspect.first_name} ${activeProspect.last_name}`.trim() ||
    activeProspect.phone
  const initials = displayName
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  // Méta appels (ligne sous le nom, façon Figma).
  const totalCalls = activeProspect.attempts || callLogs.length
  const answeredLogs = callLogs.filter((l) => l.duration > 0)
  const avgDuration = answeredLogs.length
    ? Math.round(
        answeredLogs.reduce((s, l) => s + l.duration, 0) / answeredLogs.length,
      )
    : 0
  const lastCall = callLogs.length
    ? callLogs.reduce((a, b) =>
        new Date(a.created_at) > new Date(b.created_at) ? a : b,
      )
    : null

  // Historique groupé par jour (plus récent d'abord). Clé = date complète
  // (année incluse) pour ne jamais fusionner deux jours de même jour/mois
  // d'années différentes ; le libellé reste relatif ("Hier", "15 juil.").
  const historyGroups: { key: string; label: string; logs: typeof callLogs }[] =
    []
  for (const log of [...callLogs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )) {
    const d = new Date(log.created_at)
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    const g = historyGroups.find((x) => x.key === key)
    if (g) g.logs.push(log)
    else
      historyGroups.push({
        key,
        label: relativeDay(log.created_at, dateLocale, dayLabels),
        logs: [log],
      })
  }

  return (
    <div className={cn('flex-1 flex flex-col overflow-hidden min-w-0', bg)}>
      <div className="flex-1 overflow-y-auto p-5 space-y-5 min-h-0">
        {/* Card profil */}
        <div className={cn('border rounded-lg p-5', cardBg, border)}>
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-lg font-bold',
                darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-600',
              )}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className={cn('text-xl font-bold truncate', textPrimary)}>
                {displayName}
              </h2>
              {/* Ligne "numéro · société" (design Figma) */}
              <p
                className={cn(
                  'text-sm mt-1 truncate',
                  darkMode ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                {formatPhone(activeProspect.phone)}
                {activeProspect.company_name && (
                  <>
                    {'   ·   '}
                    {activeProspect.company_name}
                  </>
                )}
              </p>
              {activeProspect.email && (
                <p
                  className={cn(
                    'text-[13px] mt-1 truncate flex items-center gap-1.5',
                    textMuted,
                  )}
                >
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  {activeProspect.email}
                </p>
              )}
            </div>
            {showBadge && badgeStyle && (
              <div
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap',
                  darkMode ? badgeStyle.badgeDark : badgeStyle.badgeLight,
                )}
              >
                {badgeLabel}
              </div>
            )}
          </div>

          {/* Ligne méta : appels · dernier appel · durée moy. */}
          <div className={cn('flex items-center gap-8 mt-4 pt-4 border-t', border)}>
            <div>
              <p className={cn('text-base font-bold leading-none', textPrimary)}>
                {totalCalls}
              </p>
              <p className={cn('text-[10px] uppercase tracking-widest mt-1', textMuted)}>
                {totalCalls !== 1 ? t('powerDialer.session.calls', 'Appels') : t('powerDialer.session.callSingular', 'Appel')}
              </p>
            </div>
            <div>
              <p className={cn('text-base font-bold leading-none', textPrimary)}>
                {lastCall ? relativeDay(lastCall.created_at, dateLocale, dayLabels) : '—'}
              </p>
              <p className={cn('text-[10px] uppercase tracking-widest mt-1', textMuted)}>
                {t('powerDialer.session.lastCall', 'Dernier appel')}
              </p>
            </div>
            <div>
              <p className={cn('text-base font-bold leading-none', textPrimary)}>
                {avgDuration ? formatDuration(avgDuration) : '—'}
              </p>
              <p className={cn('text-[10px] uppercase tracking-widest mt-1', textMuted)}>
                {t('powerDialer.session.avgDuration', 'Durée moy.')}
              </p>
            </div>
          </div>


          {/* Caller ID mémorisé pour ce contact + figer/libérer */}
          <div
            className={cn(
              'mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-lg border',
              cardBg,
              border,
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  darkMode ? 'bg-slate-800' : 'bg-slate-100',
                )}
              >
                <MapPin className={cn('w-4 h-4', textMuted)} />
              </div>
              <div className="min-w-0">
                <p className={cn('text-[10px] uppercase tracking-widest', textMuted)}>
                  {t('powerDialer.session.numberShown', 'Numéro présenté à ce contact')}
                </p>
              {pinStatus.data?.caller_id ? (
                <p className={cn('text-sm font-mono truncate', textSecondary)}>
                  {formatPhone(pinStatus.data.caller_id)}
                  {pinStatus.data.pinned && (
                    <span className="ml-2 text-[10px] text-[#F5A623]">{t('powerDialer.session.pinnedTag', 'figé')}</span>
                  )}
                </p>
              ) : (
                <p className={cn('text-xs', textMuted)}>
                  {t('powerDialer.session.setOnFirstCall', 'Fixé au premier appel')}
                </p>
              )}
              </div>
            </div>
            {pinStatus.data?.caller_id && (
              <button
                type="button"
                onClick={togglePin}
                disabled={pinCaller.isPending || unpinCaller.isPending}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 whitespace-nowrap',
                  pinStatus.data.pinned
                    ? 'border-[#F5A623]/40 text-[#F5A623] hover:bg-[#F5A623]/10'
                    : cn(border, textSecondary, 'hover:bg-slate-500/10'),
                )}
              >
                {pinStatus.data.pinned ? (
                  <>
                    <PinOff className="w-3.5 h-3.5" /> {t('powerDialer.session.unpin', 'Libérer')}
                  </>
                ) : (
                  <>
                    <Pin className="w-3.5 h-3.5" /> {t('powerDialer.session.pin', 'Figer')}
                  </>
                )}
              </button>
            )}
          </div>

          {/* Rappel programmé — visible si la disposition est de catégorie
              "callback" (marche pour les dispositions personnalisées). */}
          {disposition?.category === 'callback' && activeProspect.callback_date && (
            <div
              className={cn(
                'mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border',
                darkMode
                  ? 'bg-amber-500/10 border-amber-500/30'
                  : 'bg-amber-50 border-amber-200',
              )}
            >
              <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-[10px] uppercase tracking-widest',
                    darkMode ? 'text-amber-400/70' : 'text-amber-700/80',
                  )}
                >
                  {t('powerDialer.session.scheduledCallback', 'Rappel programmé')}
                </p>
                <p
                  className={cn(
                    'text-sm font-medium',
                    darkMode ? 'text-amber-300' : 'text-amber-800',
                  )}
                >
                  {new Date(activeProspect.callback_date).toLocaleString(
                    dateLocale,
                    {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    },
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Historique des appels */}
        <div className={cn('border rounded-lg p-5', cardBg, border)}>
          <h3
            className={cn(
              'text-xs font-medium uppercase tracking-widest mb-3',
              textMuted,
            )}
          >
            {t('powerDialer.session.callHistory', 'Historique des appels')}
          </h3>
          {callLogs.length === 0 ? (
            <div className="text-center py-6">
              <Phone className={cn('w-8 h-8 mx-auto', textFaint)} />
              <p className={cn('text-xs mt-2', textMuted)}>
                {t('powerDialer.session.noCalls', 'Aucun appel pour ce prospect')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {historyGroups.map((group) => (
                <div key={group.key}>
                  <p
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-widest mb-2',
                      textFaint,
                    )}
                  >
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.logs.map((log) => {
                      const isInbound = log.direction === 'inbound'
                      const missed = !isInbound && log.duration === 0
                      const DirIcon = isInbound ? PhoneIncoming : PhoneOutgoing
                      const label = isInbound
                        ? t('powerDialer.session.inboundCall', 'Appel entrant · répondu')
                        : missed
                          ? t('powerDialer.session.outboundNoAnswer', 'Appel sortant · sans réponse')
                          : t('powerDialer.session.outboundAnswered', 'Appel sortant · répondu')
                      return (
                        <div
                          key={log.id}
                          className={cn(
                            'flex items-center gap-3 p-2 rounded-lg transition-colors',
                            darkMode
                              ? 'hover:bg-slate-800/50'
                              : 'hover:bg-slate-100',
                          )}
                        >
                          <div
                            className={cn(
                              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                              darkMode ? 'bg-slate-800' : 'bg-slate-100',
                            )}
                          >
                            <DirIcon
                              className={cn(
                                'w-4 h-4',
                                missed
                                  ? 'text-red-400'
                                  : isInbound
                                    ? 'text-sky-400'
                                    : textMuted,
                              )}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn('text-sm truncate', textPrimary)}>
                              {label}
                            </p>
                          </div>
                          {missed ? (
                            <span className="text-[11px] font-semibold text-red-400">
                              {t('powerDialer.session.missed', 'Manqué')}
                            </span>
                          ) : (
                            <span
                              className={cn(
                                'text-[11px] font-mono px-2 py-0.5 rounded-md',
                                darkMode
                                  ? 'bg-slate-800 text-slate-300'
                                  : 'bg-slate-100 text-slate-600',
                              )}
                            >
                              {formatDuration(log.duration)}
                            </span>
                          )}
                          <span
                            className={cn('text-xs tabular-nums', textMuted)}
                          >
                            {formatTime(log.created_at, dateLocale)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
